import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const PERIODS = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'year', label: 'Cette annee' },
  { value: 'custom', label: 'Periode personnalisee' },
  { value: 'all', label: 'Tout' }
];

const DOSSIER_STATUSES = [
  { value: '', label: 'Tous les statuts' },
  { value: 'finalise', label: 'Finalises' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'annule', label: 'Annules' }
];

const formatCurrency = (value) => new Intl.NumberFormat('fr-TN', {
  style: 'currency',
  currency: 'TND',
  minimumFractionDigits: 3
}).format(Number(value || 0));

const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-';

const CommerciauxDashboard = ({ getApiUrl, commerciaux = [], onNavigate }) => {
  const [filters, setFilters] = useState({
    periode: 'month',
    dateDebut: '',
    dateFin: '',
    commercialId: '',
    statutDossier: ''
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCommercialId, setSelectedCommercialId] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(getApiUrl(`/commerciaux/dashboard?${queryString}`), {
          headers: authHeaders()
        });
        setData(response.data);
        const first = response.data?.performances?.[0]?.commercial?.id || '';
        setSelectedCommercialId(prev => prev || first);
      } catch (err) {
        setError(err.response?.data?.message || 'Impossible de charger le dashboard commerciaux.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [getApiUrl, queryString]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'periode' && value !== 'custom' ? { dateDebut: '', dateFin: '' } : {})
    }));
    if (name === 'commercialId') setSelectedCommercialId(value);
  };

  const stats = data?.stats || {};
  const performances = data?.performances || [];
  const selectedCommercial = performances.find(row => String(row.commercial.id) === String(selectedCommercialId)) || performances[0];

  const statCards = [
    { label: 'Commerciaux', value: stats.nombreTotalCommerciaux || 0, icon: 'fa-users', tone: 'blue' },
    { label: 'Dossiers affectes', value: stats.nombreTotalDossiers || 0, icon: 'fa-folder-open', tone: 'slate' },
    { label: 'Dossiers finalises', value: stats.dossiersFinalises || 0, icon: 'fa-circle-check', tone: 'green' },
    { label: 'Dossiers annules', value: stats.dossiersAnnules || 0, icon: 'fa-ban', tone: 'red' },
    { label: 'Chiffre affaires', value: formatCurrency(stats.chiffreAffaires), icon: 'fa-chart-line', tone: 'green' },
    { label: 'Commissions totales', value: formatCurrency(stats.commissionsTotales), icon: 'fa-coins', tone: 'blue' },
    { label: 'Commissions payees', value: formatCurrency(stats.commissionsPayees), icon: 'fa-money-bill-wave', tone: 'teal' },
    { label: 'Restant a payer', value: formatCurrency(stats.commissionsRestantes), icon: 'fa-hourglass-half', tone: 'orange' },
    { label: 'Sans commission', value: stats.dossiersSansCommission || 0, icon: 'fa-triangle-exclamation', tone: 'red' }
  ];

  const openDossier = (row) => {
    onNavigate('detail-dossier', { clientId: row.clientId, dossierId: row.dossierId });
  };

  const openCommercial = (commercialId) => {
    onNavigate('commercial-detail', commercialId);
  };

  const openCommissionConfig = (commercialId) => {
    onNavigate('commercial-commissions', commercialId ? { commercialId } : null);
  };

  return (
    <div className="cmd-dashboard">
      <div className="cmd-toolbar">
        <div>
          <h2>Dashboard commerciaux</h2>
          <p>Suivi dynamique des dossiers, commissions, paiements et performances commerciales.</p>
        </div>
        <span>{loading ? 'Chargement...' : `Mis a jour ${formatDate(data?.generatedAt)}`}</span>
      </div>

      <div className="cmd-filters">
        <label>
          <span>Periode</span>
          <select value={filters.periode} onChange={(e) => handleFilterChange('periode', e.target.value)}>
            {PERIODS.map(period => <option key={period.value} value={period.value}>{period.label}</option>)}
          </select>
        </label>
        {filters.periode === 'custom' && (
          <>
            <label>
              <span>Debut</span>
              <input type="date" value={filters.dateDebut} onChange={(e) => handleFilterChange('dateDebut', e.target.value)} />
            </label>
            <label>
              <span>Fin</span>
              <input type="date" value={filters.dateFin} onChange={(e) => handleFilterChange('dateFin', e.target.value)} />
            </label>
          </>
        )}
        <label>
          <span>Commercial</span>
          <select value={filters.commercialId} onChange={(e) => handleFilterChange('commercialId', e.target.value)}>
            <option value="">Tous les commerciaux</option>
            {commerciaux.map(com => <option key={com._id} value={com._id}>{com.prenom} {com.nom}</option>)}
          </select>
        </label>
        <label>
          <span>Statut dossier</span>
          <select value={filters.statutDossier} onChange={(e) => handleFilterChange('statutDossier', e.target.value)}>
            {DOSSIER_STATUSES.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
          </select>
        </label>
      </div>

      {error && <div className="cmd-error">{error}</div>}

      <div className="cmd-stat-grid">
        {statCards.map(card => (
          <div className="cmd-stat-card" key={card.label}>
            <div className={`cmd-stat-icon ${card.tone}`}><i className={`fas ${card.icon}`}></i></div>
            <div>
              <strong>{card.value}</strong>
              <span>{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      {loading && <LoadingSpinner message="Chargement du dashboard commerciaux..." />}

      <div className="cmd-panels">
        <section className="cmd-panel cmd-panel-wide">
          <div className="cmd-panel-header">
            <h3>Dossiers Sans Commission</h3>
            <span>{data?.dossiersSansCommission?.length || 0} dossier(s)</span>
          </div>
          <div className="cmd-table-wrap">
            <table className="cmd-table">
              <thead>
                <tr>
                  <th>Commercial</th>
                  <th>Client</th>
                  <th>Dossier</th>
                  <th>Puissance / type</th>
                  <th>Montant</th>
                  <th>Date</th>
                  <th>Raison</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.dossiersSansCommission || []).length === 0 ? (
                  <tr><td colSpan="8" className="cmd-empty">Aucun dossier sans commission pour ces filtres.</td></tr>
                ) : data.dossiersSansCommission.map(row => (
                  <tr key={`${row.clientId}-${row.dossierId}`}>
                    <td>{row.commercial?.nom || '-'}</td>
                    <td>{row.client || '-'}</td>
                    <td>{row.referenceDossier || '-'}</td>
                    <td>{row.puissance || 0} kWc {row.typeProduit ? `- ${row.typeProduit}` : ''}</td>
                    <td>{formatCurrency(row.montant)}</td>
                    <td>{formatDate(row.date)}</td>
                    <td className="cmd-reason">{row.raisonSansCommission || '-'}</td>
                    <td>
                      <div className="cmd-actions">
                        <button onClick={() => openDossier(row)} title="Acceder au dossier"><i className="fas fa-folder-open"></i></button>
                        <button onClick={() => openCommissionConfig(row.commercial?.id)} title="Configurer les commissions"><i className="fas fa-sliders"></i></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="cmd-panel">
          <div className="cmd-panel-header">
            <h3>Commissions a payer</h3>
            <span>{data?.commissionsAPayer?.length || 0}</span>
          </div>
          <div className="cmd-commission-list">
            {(data?.commissionsAPayer || []).map(row => (
              <button key={row.commercial.id} className="cmd-commission-row" onClick={() => setSelectedCommercialId(row.commercial.id)}>
                <strong>{row.commercial.nom}</strong>
                <span>Total: {formatCurrency(row.commissionTotale)}</span>
                <span>Paye: {formatCurrency(row.commissionPayee)}</span>
                <em>Reste: {formatCurrency(row.commissionRestante)}</em>
              </button>
            ))}
          </div>
        </section>

        <section className="cmd-panel">
          <div className="cmd-panel-header">
            <h3>Classement performance</h3>
            <span>Conversion + CA + volume</span>
          </div>
          <div className="cmd-ranking">
            {(data?.classement || []).map((row, index) => (
              <button key={row.commercial.id} className="cmd-rank-row" onClick={() => setSelectedCommercialId(row.commercial.id)}>
                <strong>{index + 1}. {row.commercial.nom}</strong>
                <span>{row.tauxConversion}% conversion</span>
                <span>{formatCurrency(row.chiffreAffaires)}</span>
                <span>{row.nombreDossiers} dossier(s)</span>
              </button>
            ))}
          </div>
        </section>

        <section className="cmd-panel cmd-panel-wide">
          <div className="cmd-panel-header">
            <h3>Fiche detaillee par commercial</h3>
            <select value={selectedCommercial?.commercial?.id || ''} onChange={(e) => setSelectedCommercialId(e.target.value)}>
              {performances.map(row => <option key={row.commercial.id} value={row.commercial.id}>{row.commercial.nom}</option>)}
            </select>
          </div>
          {selectedCommercial ? (
            <>
              <div className="cmd-detail-grid">
                <div><span>Dossiers affectes</span><strong>{selectedCommercial.nombreDossiers}</strong></div>
                <div><span>Finalises</span><strong>{selectedCommercial.dossiersFinalises}</strong></div>
                <div><span>En cours</span><strong>{selectedCommercial.dossiersEnCours}</strong></div>
                <div><span>Annules</span><strong>{selectedCommercial.dossiersAnnules}</strong></div>
                <div><span>Taux conversion</span><strong>{selectedCommercial.tauxConversion}%</strong></div>
                <div><span>CA genere</span><strong>{formatCurrency(selectedCommercial.chiffreAffaires)}</strong></div>
                <div><span>Panier moyen</span><strong>{formatCurrency(selectedCommercial.panierMoyen)}</strong></div>
                <div><span>Commission generee</span><strong>{formatCurrency(selectedCommercial.commissionTotale)}</strong></div>
                <div><span>Commission payee</span><strong>{formatCurrency(selectedCommercial.commissionPayee)}</strong></div>
                <div><span>Commission restante</span><strong>{formatCurrency(selectedCommercial.commissionRestante)}</strong></div>
              </div>
              <div className="cmd-evolution">
                {(selectedCommercial.evolution || []).length === 0 ? (
                  <p>Aucune evolution disponible pour cette periode.</p>
                ) : selectedCommercial.evolution.map(row => (
                  <div key={row.periode} className="cmd-evolution-row">
                    <span>{row.periode}</span>
                    <strong>{row.nombreDossiers} dossier(s)</strong>
                    <em>{formatCurrency(row.chiffreAffaires)}</em>
                  </div>
                ))}
              </div>
              <button className="cmd-open-detail" onClick={() => openCommercial(selectedCommercial.commercial.id)}>
                <i className="fas fa-user-chart"></i> Ouvrir la fiche commercial
              </button>
            </>
          ) : (
            <div className="cmd-empty">Aucun commercial disponible.</div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CommerciauxDashboard;
